import { z } from 'zod'
import { MappingType, OperationType as AccountOperationType } from '../../../../models/Account'

export const SessionMappingType = z.enum(['PREDEFINED', 'CUSTOM'])
export const DescriptorType = z.enum(['static', 'field', 'variable'])
export const FieldUpdateMode = z.enum(['append', 'prepend', 'overwrite', 'create_only'])
export const GroupOperator = z.enum(['and', 'or'])
export const OperationType = z.enum(['create', 'update', 'fetch'])
export const FilterOperator = z.enum(['eq', 'ne', 'gt', 'lt', 'ge', 'le', 'in'])
export const Direction = z.enum(['ASC', 'DESC'])

export const FieldConstraintSchema = z.object({
  maxLength: z.number().nullable().optional(),
  valueType: z.string().nullable().optional(),
  valueFormat: z.string().nullable().optional(),
})

export const OrderBySchema = z.object({
  field: z.string(),
  direction: Direction,
})

export const ConditionValueSchema = z.object({
  value: z.string().nullable().optional(),
  type: DescriptorType,
  object: z.string().nullable().optional(),
  field: z.string().nullable().optional(),
  valueType: z.string().nullable().optional(),
  valueFormat: z.string().nullable().optional(),
})

export const FilterExpressionSchema = z.object({
  field: z.string(),
  operator: FilterOperator,
  source: ConditionValueSchema,
  valueType: z.string().nullable().optional(),
  valueFormat: z.string().nullable().optional(),
})

export const FilterSchema = z.object({
  groupOperator: GroupOperator,
  expressions: z.array(FilterExpressionSchema),
})

export const ConditionSchema = z.object({
  subject: ConditionValueSchema,
  operator: FilterOperator,
  source: ConditionValueSchema.nullable().optional(),
})

type ConditionGroup = {
  operator?: 'and' | 'or' | null
  conditions?: z.infer<typeof ConditionSchema>[] | null
  returnValue?: z.infer<typeof ConditionValueSchema> | null
  fallback?: ConditionGroup | z.infer<typeof ConditionValueSchema> | null
}

export const ConditionGroupSchema: z.ZodType<ConditionGroup> = z.lazy(() =>
  z.object({
    operator: GroupOperator.nullable().optional(),
    conditions: z.array(ConditionSchema).nullable().optional(),
    returnValue: ConditionValueSchema.nullable().optional(),
    fallback: z.union([ConditionGroupSchema, ConditionValueSchema]).nullable().optional(),
  }),
)

// ---------------------------------------------------------
// 4. DESCRIPTORS
// ---------------------------------------------------------

export const FieldDescriptorSchema = z.object({
  value: z.string().nullable().optional(),
  type: DescriptorType,
  valueType: z.string().nullable().optional(),
  valueFormat: z.string().nullable().optional(),
  updateMode: FieldUpdateMode.nullable().optional(),
  conditions: ConditionGroupSchema.nullable().optional(),
  defaultValue: z.string().nullable().optional(),
})

const RelationshipBase = z.object({
  object: z.string().nullable().optional(),
  field: z.string().nullable().optional(),
})

export const RelationshipTargetSchema = RelationshipBase.extend({
  valueType: z.string().nullable().optional(),
  valueFormat: z.string().nullable().optional(),
})

export const RelationshipSourceSchema = RelationshipBase.extend({
  filters: z.array(FilterSchema).nullable().optional(),
})

export const ContactLinkSchema = z.object({
  source: RelationshipSourceSchema.nullable().optional(),
  target: RelationshipTargetSchema.nullable().optional(),
})

export const ContactSyncSchema = z.object({
  filters: z.array(FilterSchema).nullable().optional(),
  applyOwnerFilter: z.boolean().nullable().optional(),
})

export const RelatedObjectSchema = z.object({
  include: z.boolean().nullable().optional(),
  dependency: z.array(z.string()).nullable().optional(),
  limit: z.number().nullable().optional(),
  outputFields: z.array(z.record(z.string(), z.string())).nullable().optional(),
  orderBy: OrderBySchema.nullable().optional(),
  filters: z.array(FilterSchema).nullable().optional(),
  fields: z.record(z.string(), z.array(FieldDescriptorSchema)).nullable().optional(),
  operationType: OperationType.nullable().optional(),
  isHousehold: z.boolean().nullable().optional(),
  primaryIdAttribute: z.string().nullable().optional(),
})

export const DetailedCRMAccountIntegrationMappingSchema = z.object({
  objectName: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  fields: z.record(z.string(), z.array(FieldDescriptorSchema)).nullable().optional(),
  // Modified to strictly allow boolean for saveToHousehold logic or record for general usage
  additionalInformation: z.record(z.string(), z.unknown()).nullable().optional(),
  filters: z.array(FilterSchema).nullable().optional(),
  sync: ContactSyncSchema.nullable().optional(),
  relatedObjects: z.record(z.string(), RelatedObjectSchema).nullable().optional(),
  operationType: OperationType.nullable().optional(),
  contactLinks: z.array(ContactLinkSchema).nullable().optional(),
  mappingType: SessionMappingType.nullable().optional(),
  constraints: z.record(z.string(), FieldConstraintSchema).nullable().optional(),
  limit: z.number().nullable().optional(),
  isHousehold: z.boolean().nullable().optional(),
})

const MappingsSchema = z.object({
  session: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  note: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  result: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  contact: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  documentSummary: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  task: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  additionalObjects: z.array(DetailedCRMAccountIntegrationMappingSchema.nullable().optional()).nullable().optional(),
})

const BaseIntegrationSettingsSchema = z.object({
  type: z.string().optional(),
  serviceId: z.string(),
  loginUrl: z.string().nullable().optional(),
  mappings: MappingsSchema, // Nested mappings
  enableNoteExport: z.boolean().nullable().optional(),
  enableFullMeetingExport: z.boolean().nullable().optional(),
  isCustom: z.boolean().nullable().optional(),
  isSyncDisabled: z.boolean().nullable().optional(),
})

export const getIntegrationSettingsSchema = (additionalObjectsLimit: number) => {
  return BaseIntegrationSettingsSchema.superRefine((data, ctx) => {
    const mappings = data.mappings

    if (!mappings.contact) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Contact mapping is required',
        path: ['mappings', 'contact'],
      })
    }

    if (!mappings.session) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Meeting Summary mapping is required',
        path: ['mappings', 'session'],
      })
    }

    if (mappings.contact) {
      if (!mappings.contact.objectName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Contact object name is required',
          // Path updated to include 'mappings'
          path: ['mappings', 'contact', 'objectName'],
        })
      }

      const contactFields = mappings.contact.fields

      // 2. Look for the email mapping
      const emailDescriptors = contactFields?.['email']
      const emailValue = emailDescriptors?.[0]?.value

      // 3. Validation Logic
      if (mappings.contact.mappingType && (!emailValue || emailValue.trim() === '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'E-mail mapping is required',
          // Path updated to include 'mappings'
          path: ['mappings', 'contact', 'fields', 'email'],
        })
      }
    }

    if (mappings.session) {
      if (!mappings.session.objectName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Meeting Summary object name is required',
          // Path updated to include 'mappings'
          path: ['mappings', 'session', 'objectName'],
        })
      }

      const contactFields = mappings.contact?.fields

      const additionalInfo = mappings.session.additionalInformation as Record<string, unknown> | undefined
      const saveToHousehold = additionalInfo?.saveToHousehold === true

      const isPredefinedOrEmpty = mappings.session.mappingType === 'PREDEFINED' || !mappings.session.mappingType

      if (isPredefinedOrEmpty && saveToHousehold) {
        const householdDescriptors = contactFields?.['household']
        const householdValue = householdDescriptors?.[0]?.value

        if (!householdValue || householdValue.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Household field must be mapped when "Save to Household" is enabled in Session settings.',
            path: ['mappings', 'contact', 'fields', 'household'],
          })
        }
      }

      if (mappings.session.mappingType === MappingType.CUSTOM) {
        if (Object.keys(mappings.session.contactLinks || {}).length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one Contact Linking must be defined for Session when using Custom mapping type.',
            path: ['mappings', 'session', 'contactLinks'],
          })
        }

        if (mappings.session.operationType === 'update') {
          const updateTitle = (mappings.session.additionalInformation?.update as { title?: string })?.title
          if (updateTitle === undefined || updateTitle === null) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Title field must be defined if type is set to UPDATE.',
              path: ['mappings', 'session', 'additionalInformation', 'update', 'title'],
            })
          }
        }

        if (mappings.session.contactLinks && mappings.session.contactLinks.length > 0) {
          const notFullyDefinedLinks = mappings.session.contactLinks.filter((link) => {
            const sourceObj = link.source?.object
            const sourceField = link.source?.field
            const targetObj = link.target?.object
            const targetField = link.target?.field
            return !sourceObj || !sourceField || !targetObj || !targetField
          })
          if (notFullyDefinedLinks.length > 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'All Contact Linking source and target objects and fields must be fully defined.',
              path: ['mappings', 'session', 'contactLinks'],
            })
          }
        }

        if (Object.keys(mappings.session.fields || {}).length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one field must be mapped for Session when using Custom mapping type.',
            path: ['mappings', 'session', 'fields'],
          })
        }

        if (mappings.session.operationType === AccountOperationType.UPDATE) {
          const sessionFieldsWithoutUpdateType = Object.values(mappings.session.fields || {})
            .flat()
            .filter((descriptor) => !descriptor.updateMode)

          if (sessionFieldsWithoutUpdateType.length > 0) {
            sessionFieldsWithoutUpdateType.forEach((descriptor) => {
              const fieldName = Object.entries(mappings.session?.fields || {}).find(([, descriptors]) => descriptors.includes(descriptor))?.[0]
              if (fieldName) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'All Session fields must have an update mode defined when operation type is set to UPDATE.',
                  path: ['mappings', 'session', 'fields', fieldName],
                })
              }
            })
          }
        }

        const sessionFieldsNothingMapped = Object.entries(mappings.session.fields || {}).reduce<string[]>((acc, [fieldName, descriptors]) => {
          if (descriptors.length === 0) {
            return [...acc, fieldName]
          }
          return acc
        }, [])
        if (sessionFieldsNothingMapped.length > 0) {
          sessionFieldsNothingMapped.forEach((fieldName) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'All Session fields must have at least one mapped value when using Custom mapping type.',
              path: ['mappings', 'session', 'fields', fieldName],
            })
          })
        }
      }
    }

    if (mappings.task) {
      if (!mappings.task.objectName && mappings.task.mappingType === MappingType.CUSTOM) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Task object name is required',
          path: ['mappings', 'task', 'objectName'],
        })
      }

      if (mappings.task.mappingType === MappingType.CUSTOM) {
        if (Object.keys(mappings.task.contactLinks || {}).length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one Contact Linking must be defined for Task when using Custom mapping type.',
            path: ['mappings', 'task', 'contactLinks'],
          })
        }

        if (mappings.task.contactLinks && mappings.task.contactLinks.length > 0) {
          const notFullyDefinedLinks = mappings.task.contactLinks.filter((link) => {
            const sourceObj = link.source?.object
            const sourceField = link.source?.field
            const targetObj = link.target?.object
            const targetField = link.target?.field
            return !sourceObj || !sourceField || !targetObj || !targetField
          })
          if (notFullyDefinedLinks.length > 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'All Contact Linking source and target objects and fields must be fully defined.',
              path: ['mappings', 'task', 'contactLinks'],
            })
          }
        }

        if (Object.keys(mappings.task.fields || {}).length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one field must be mapped for Task when using Custom mapping type.',
            path: ['mappings', 'task', 'fields'],
          })
        }

        const taskFieldsNothingMapped = Object.entries(mappings.task.fields || {}).reduce<string[]>((acc, [fieldName, descriptors]) => {
          if (descriptors.length === 0) {
            return [...acc, fieldName]
          }
          return acc
        }, [])
        if (taskFieldsNothingMapped.length > 0) {
          taskFieldsNothingMapped.forEach((fieldName) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'All Task fields must have at least one mapped value when using Custom mapping type.',
              path: ['mappings', 'task', 'fields', fieldName],
            })
          })
        }
      }
    }

    if (mappings.additionalObjects) {
      const additionalObjectMappingsWithoutObjectName = mappings.additionalObjects.filter((obj) => !obj?.objectName && obj?.mappingType === MappingType.CUSTOM)
      if (additionalObjectMappingsWithoutObjectName.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All Additional Objects must have an object defined.',

          path: ['mappings', 'additionalObjects'],
        })
      }

      const additionalObjectMappingWithoutFields = mappings.additionalObjects.filter((obj) => {
        return obj?.mappingType === MappingType.CUSTOM && (!obj.fields || Object.keys(obj.fields).length === 0)
      })
      if (additionalObjectMappingWithoutFields.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All Additional Objects must have at least one field mapped.',
          path: ['mappings', 'additionalObjects'],
        })
      }

      const sumAdditionalObjectsCount = mappings.additionalObjects ? mappings.additionalObjects.map((obj) => obj?.limit || 0).reduce((a, b) => a + b, 0) : 0

      if (sumAdditionalObjectsCount > additionalObjectsLimit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `The total limit for Additional Objects exceeds the allowed maximum of ${additionalObjectsLimit}.`,
          path: ['mappings', 'additionalObjects'],
        })
      }

      const objectWithZeroLimit = mappings.additionalObjects.find((obj) => (obj?.limit || 0) < 1)
      if (objectWithZeroLimit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Each Additional Object must have a limit of at least 1.',
          path: ['mappings', 'additionalObjects'],
        })
      }

      if (mappings.additionalObjects.some((obj) => !obj?.contactLinks || obj.contactLinks.length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one Contact Linking must be defined for the object.',
          path: ['mappings', 'additionalObjects'],
        })
      }

      const additionalObjectWithoutProperContactLinking = mappings.additionalObjects.some((obj) => {
        return obj?.contactLinks?.some((link) => {
          return link.source?.object === null || link.source?.field === null || link.target?.object === null || link.target?.field === null
        })
      })
      if (additionalObjectWithoutProperContactLinking) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All Contact Linking source and target objects and fields must be fully defined.',
          path: ['mappings', 'additionalObjects'],
        })
      }
    }
  })
}
export type IntegrationSettings = z.infer<typeof BaseIntegrationSettingsSchema>
